import { useCallback, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { UnauthorizedError } from '../auth/authFetch';
import RichTextEditor from './RichTextEditor';
import FeaturedImage from './FeaturedImage';
import PreviewPane from './PreviewPane';
import { loadPreviewOpen, savePreviewOpen } from './previewPref';
import { useAutosave } from './useAutosave';
import {
  draftKey,
  persistDraftLocal,
  loadStoredDraft,
  clearDraftLocal,
  DraftSnapshot,
} from './draftStorage';
import { recoverDraft } from './recoverDraft';
import { bodyHasPendingUpload } from './bodyImages';
import {
  createPost,
  createDraft,
  autosavePost,
  updatePost,
  deletePost,
  uploadImage,
  excerptFromHtml,
  countWords,
  Post,
  PostPayload,
} from './api';

type Status = { kind: 'idle' } | { kind: 'saving' } | { kind: 'saved' } | { kind: 'error'; message: string };

interface Props {
  initialPost?: Post;
  // postId is the row the editor has been autosaving (an existing post, or one
  // auto-created on first autosave) so the parent promotes it rather than creating
  // a duplicate. Undefined when no autosave has run yet.
  onSave?: (payload: PostPayload, postId?: number) => void | Promise<void>;
  onCancel?: () => void;
}

// authFetch throws UnauthorizedError when a 401 can't be recovered by refresh.
function isUnauthorized(err: unknown): boolean {
  return err instanceof UnauthorizedError;
}

function fmtTime(d: Date): string {
  let h = d.getHours();
  const m = d.getMinutes();
  const ap = h >= 12 ? 'pm' : 'am';
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, '0')}${ap}`;
}

export default function PostEditor({ initialPost, onSave, onCancel }: Props) {
  const { signOut } = useAuth();
  const isNew = !initialPost;
  // Fixed at mount: an existing post uses its id key; a new post uses the shared
  // `new` key for the whole session (the editor never remounts to an id key).
  const localKey = draftKey(initialPost?.id ?? 'new');

  // Recover the newest of {local, backend draft, live post} once at mount.
  const recovered = useRef(recoverDraft(initialPost, loadStoredDraft(localKey)));
  const init = recovered.current.snapshot;

  // The backend row being autosaved: an existing post, or one auto-created on first
  // autosave. Undefined until then. creatingRef guards against a double-create from
  // overlapping autosaves; autoCreatedRef tracks a phantom to reclaim on cancel.
  const [postId, setPostId] = useState<number | undefined>(initialPost?.id);
  const creatingRef = useRef(false);
  const autoCreatedRef = useRef(false);

  const [title, setTitle] = useState(init.title);
  const [featuredImageUrl, setFeaturedImageUrl] = useState<string | undefined>(
    init.featured_image_url ?? undefined,
  );
  const [bodyHtml, setBodyHtml] = useState(init.body_html);
  const [published, setPublished] = useState(init.published);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showRecovered, setShowRecovered] = useState(recovered.current.differsFromLive);
  const [previewOpen, setPreviewOpen] = useState(loadPreviewOpen);

  // Persist the choice globally so it survives reloads and applies to every post.
  const togglePreview = (open: boolean) => {
    setPreviewOpen(open);
    savePreviewOpen(open);
  };

  const canSave =
    title.trim().length > 0 &&
    bodyHtml.trim().length > 0 &&
    status.kind !== 'saving' &&
    !uploadingImage && // a featured blob: preview is in flight
    !bodyHasPendingUpload(bodyHtml); // a body image upload is in flight
  const words = countWords(bodyHtml);

  // The optimistic preview uses a local blob: URL that must never be persisted
  // (it's dead after reload) or saved as featured_image_url.
  const storedImageUrl =
    featuredImageUrl && !featuredImageUrl.startsWith('blob:') ? featuredImageUrl : undefined;

  // Autosave: a fast silent local writer (crash safety) and a slower backend writer
  // that drives the visible status. Memoize the snapshot + persisters so each effect
  // only fires on a real change.
  const draftSnapshot = useMemo<DraftSnapshot>(
    () => ({ title, featured_image_url: storedImageUrl ?? null, body_html: bodyHtml, published }),
    [title, storedImageUrl, bodyHtml, published],
  );

  const persistLocal = useCallback((d: DraftSnapshot) => persistDraftLocal(localKey, d), [localKey]);
  const localAutosave = useAutosave(draftSnapshot, persistLocal, 800);

  // Keep signOut out of persistBackend's deps: some consumers (and tests) hand back a
  // fresh signOut every render, which would otherwise re-create persistBackend each
  // render and spin useAutosave's effect into an update loop.
  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;

  // First run for a brand-new post creates the row (guarded against overlap), then
  // PATCHes the autosave endpoint (which writes the live columns for a draft, or the
  // shadow for a published post). Throwing flips the visible pill to "Saving offline…".
  const persistBackend = useCallback(
    async (d: DraftSnapshot) => {
      try {
        if (postId === undefined) {
          if (creatingRef.current) return; // a create is already in flight
          creatingRef.current = true;
          const createdPost = await createDraft(d);
          autoCreatedRef.current = true;
          setPostId(createdPost.id);
        } else {
          await autosavePost(postId, d);
        }
      } catch (err) {
        if (isUnauthorized(err)) {
          signOutRef.current();
          return; // swallow — signing out, don't flag offline
        }
        throw err; // let useAutosave show 'error' (offline); local copy already saved
      } finally {
        creatingRef.current = false;
      }
    },
    [postId],
  );
  const backendAutosave = useAutosave(draftSnapshot, persistBackend, 2000);

  const cancelAutosaves = () => {
    localAutosave.cancel();
    backendAutosave.cancel();
  };

  const autosaveLabel =
    backendAutosave.status === 'saving'
      ? 'Saving…'
      : backendAutosave.status === 'error'
        ? 'Saving offline…'
        : backendAutosave.savedAt
          ? `Draft saved · ${fmtTime(backendAutosave.savedAt)}`
          : 'Autosave on';

  const upload = async (file: File): Promise<string> => {
    const { url } = await uploadImage(file);
    return url;
  };

  const onSelectFeatured = async (file: File) => {
    // Show the chosen file instantly, then upload in the background and swap to
    // the stored URL when it lands. Save stays disabled until then.
    const localUrl = URL.createObjectURL(file);
    setFeaturedImageUrl(localUrl);
    setUploadingImage(true);
    try {
      setFeaturedImageUrl(await upload(file));
    } catch (err) {
      setFeaturedImageUrl(undefined); // revert the optimistic preview
      if (isUnauthorized(err)) {
        signOut();
        return;
      }
      setStatus({ kind: 'error', message: 'Featured image upload failed.' });
    } finally {
      URL.revokeObjectURL(localUrl);
      setUploadingImage(false);
    }
  };

  const onSaveClick = async () => {
    setStatus({ kind: 'saving' });
    try {
      const payload: PostPayload = {
        title: title.trim(),
        excerpt: excerptFromHtml(bodyHtml) || undefined,
        featured_image_url: storedImageUrl,
        body_html: bodyHtml,
        published,
      };
      // An auto-created draft already has a row -> promote it (PUT) rather than
      // create a duplicate. The parent reconciles list state; standalone falls back
      // to a direct call.
      if (onSave) {
        await onSave(payload, postId);
      } else if (postId !== undefined) {
        await updatePost(postId, payload);
      } else {
        await createPost(payload);
      }
      cancelAutosaves();
      clearDraftLocal(localKey);
      setStatus({ kind: 'saved' });
    } catch (err) {
      if (isUnauthorized(err)) {
        signOut();
        return;
      }
      setStatus({ kind: 'error', message: 'Save failed. Please try again.' });
    }
  };

  const onCancelClick = async () => {
    cancelAutosaves();
    clearDraftLocal(localKey);
    // Reclaim a never-saved auto-created phantom so it doesn't linger as a row.
    if (autoCreatedRef.current && postId !== undefined && !title.trim() && !published) {
      try {
        await deletePost(postId);
      } catch {
        // best-effort cleanup — the list already hides untitled drafts
      }
    }
    onCancel?.();
  };

  const discardRecovered = () => {
    setShowRecovered(false);
    // Reset to the live post when editing, or to an empty form for a new post —
    // either way the recovered content must leave the fields without a reload.
    setTitle(initialPost?.title ?? '');
    setFeaturedImageUrl(initialPost?.featured_image_url ?? undefined);
    setBodyHtml(initialPost?.body_html ?? '');
    setPublished(initialPost?.published ?? false);
    clearDraftLocal(localKey);
  };

  const labelCls = 'mb-2 block font-trirong_light text-sm text-[#5a6470]';
  const inputCls =
    'w-full rounded-[10px] border border-field bg-white px-4 py-3 font-trirong_light text-[16px] text-[#353f2a] ' +
    'placeholder:text-[#aeb59a] transition-colors focus:border-lime-700 focus:outline-none focus:ring-2 focus:ring-lime-600/20';

  return (
    <div>
      {showRecovered && (
        <div className="mb-5 flex items-center justify-between gap-4 rounded-xl bg-[#fbf6e6] px-5 py-3">
          <span className="font-trirong_light text-sm text-[#7a6a3a]">
            Recovered an unsaved draft.
          </span>
          <button
            type="button"
            onClick={discardRecovered}
            className="font-trirong_reg text-sm text-lime-700 underline-offset-2 hover:underline"
          >
            Discard
          </button>
        </div>
      )}

      <div className="mb-8">
        <h1 className="font-trirong_reg text-[36px] leading-none text-lime-700">
          {isNew ? 'New blog' : 'Edit blog'}
        </h1>
      </div>

      <div className="rounded-2xl bg-white p-6 shadow-card md:p-9">
        {!previewOpen && (
          <div className="mb-4 hidden justify-end lg:flex">
            <button
              type="button"
              onClick={() => togglePreview(true)}
              className="rounded-full border border-field px-4 py-1.5 font-trirong_reg text-[13px] text-lime-700
                         transition-colors hover:border-lime-700 hover:bg-[#fbfdf4] focus:outline-none
                         focus:ring-2 focus:ring-lime-600/40"
            >
              Show preview
            </button>
          </div>
        )}
        <div
          className={
            'grid grid-cols-1 gap-10 ' +
            (previewOpen ? 'lg:grid-cols-[minmax(0,1fr)_minmax(0,0.92fr)]' : 'lg:grid-cols-1')
          }
        >
          <div>
            <div className="mb-6">
              <label className={labelCls} htmlFor="title">Title</label>
              <input
                id="title"
                className={inputCls}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Give your blog a title"
              />
            </div>

            <div className="mb-6">
              <label className={labelCls}>Featured image</label>
              <FeaturedImage
                value={featuredImageUrl}
                onSelect={onSelectFeatured}
                onRemove={() => setFeaturedImageUrl(undefined)}
              />
              {uploadingImage && (
                <p className="mt-2 font-trirong_light text-[13px] text-meta">Uploading…</p>
              )}
            </div>

            <div className="mb-6">
              <label className={labelCls}>Body</label>
              <RichTextEditor
                value={bodyHtml}
                onChange={setBodyHtml}
                uploadImage={upload}
                onImageUploadError={(err) => {
                  if (isUnauthorized(err)) {
                    signOut();
                    return;
                  }
                  setStatus({ kind: 'error', message: 'Image upload failed.' });
                }}
              />
              <div className="mt-2.5 flex items-center justify-between gap-3.5">
                <span className="font-trirong_light text-[13px] text-meta">
                  {words} {words === 1 ? 'word' : 'words'}
                </span>
                <span className="inline-flex items-center gap-2 font-trirong_light text-[13px] text-meta">
                  <span
                    className={
                      'inline-block h-[7px] w-[7px] rounded-full ' +
                      (backendAutosave.status === 'saving'
                        ? 'bg-[#d8b65a]'
                        : backendAutosave.status === 'error'
                          ? 'bg-[#c98a8a]'
                          : 'bg-[#9db39f]')
                    }
                  />
                  {autosaveLabel}
                </span>
              </div>
            </div>

            <div className="mt-7 flex items-center justify-between gap-[18px] rounded-xl bg-[#f1f4e1] px-5 py-4">
              <div>
                <div className="font-trirong_reg text-[16px] text-gray-800">Publish</div>
                <div className="mt-0.5 font-trirong_light text-[13px] text-meta">
                  {published ? 'Live on your website' : 'Stored as a private draft'}
                </div>
              </div>
              <label htmlFor="publish" className="relative inline-flex shrink-0 cursor-pointer items-center">
                <input
                  id="publish"
                  type="checkbox"
                  aria-label="Publish"
                  checked={published}
                  onChange={(e) => setPublished(e.target.checked)}
                  className="peer sr-only"
                />
                <span className="h-7 w-[50px] rounded-full bg-[#cfd4c0] transition-colors peer-checked:bg-lime-700 peer-focus-visible:ring-2 peer-focus-visible:ring-lime-600/40" />
                <span className="pointer-events-none absolute left-[3px] top-[3px] h-[22px] w-[22px] rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-[22px]" />
              </label>
            </div>

            <div className="mt-8 flex items-center gap-3.5">
              <button
                type="button"
                disabled={!canSave}
                onClick={onSaveClick}
                className="rounded-full bg-lime-700 px-6 py-2.5 font-trirong_reg text-[#f7faec] shadow-sm transition-colors
                           hover:bg-lime-800 focus:outline-none focus:ring-2 focus:ring-lime-600/40
                           disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-lime-700"
              >
                {status.kind === 'saving' ? 'Saving…' : 'Save'}
              </button>

              {onCancel && (
                <button
                  type="button"
                  onClick={onCancelClick}
                  className="rounded-full border border-field px-6 py-2.5 font-trirong_reg text-lime-700
                             transition-colors hover:border-lime-700 hover:bg-[#fbfdf4] focus:outline-none
                             focus:ring-2 focus:ring-lime-600/40"
                >
                  Cancel
                </button>
              )}

              {status.kind === 'saved' && (
                <p className="font-trirong_light text-sm text-lime-700">Blog saved.</p>
              )}
              {status.kind === 'error' && (
                <p className="rounded-lg bg-red-50 px-3 py-1.5 font-trirong_light text-sm text-danger">
                  {status.message}
                </p>
              )}
            </div>
          </div>

          {previewOpen && (
            <PreviewPane
              title={title}
              imageUrl={featuredImageUrl ?? null}
              bodyHtml={bodyHtml}
              onClose={() => togglePreview(false)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
